/* global React */
/* eslint-disable no-restricted-globals */
import { useCallback, useRef, useState } from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, useTheme } from '@mui/material'
import { formatFilesize } from 'lib/util/attachment'
import { useMemoRef, useStyles } from 'platform/react/hook'
import Icon from 'ui/Element/Icon'
import Button from 'ui/Element/Button'
import Text from 'ui/Element/Text'
import Image, { type ImageProps, type ImageState } from 'ui/Element/Image'

export interface FullScreenProps extends ImageProps {
  /** label for the close button */
  closeLabel?: string
  /** label for the download button */
  downloadLabel?: string
}

/**
 * Returns the fullscreen element's current theme styles.
 *
 * @param theme the application's current theme
 * @returns     the fullscreen's styles
 */
const styles = (theme: any) => ({
  header: {
    height: '3.75rem',
    borderBottom: `0.0625rem solid ${theme.palette.gray[900]}`,
    padding: '0.75rem 1rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    columnGap: '0.5rem',
    marginRight: 'auto',
  },
  content: {
    overflow: 'hidden',
    padding: '1rem 1rem 0 1rem !important',
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    columnGap: '1rem',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '0.5rem',
    cursor: 'pointer',
  },
})

/**
 * Downloads `src` as a file called `name` by clicking a transient anchor
 * element.
 *
 * @param src  blob- or external url of the attachment
 * @param name name to download the attachment as
 */
const download = (src: string, name: string) => {
  const anchor = document.createElement('a')
  anchor.href = src
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

/**
 * FullScreen Image Element
 *
 * Renders an {@link Image} that, once loaded and clicked, opens a fullscreen
 * modal for better visibility, with an option to download the image.
 *
 * @param props               the element's properties, forwarded to {@link Image}
 * @param props.ref           a reference to the root element
 * @param props.closeLabel    label of the modal's close button
 * @param props.downloadLabel label of the modal's download button
 * @param props.events        event handlers, `onState` is tapped into
 * @returns                   the new fullscreen image element
 */
const FullScreen = ({ ref, closeLabel, downloadLabel, ...props }: FullScreenProps) => {
  const { events = {}, ...imageProps } = props

  const theme = useTheme() as any
  const classes = useStyles(styles)() as Record<string, string>

  const [state, setState] = useState<ImageState | null>(null)
  const [open, setOpen] = useState(false)

  // The inner {@link Image} is memoized against its source props, so the
  // handlers it receives are the ones from the first render. The current
  // loading state is therefore kept in a ref for them to read.
  const loadedRef = useRef(false)

  const loaded = state?.status === 'loaded' && !!state?.src
  const title = state?.name || ''

  /**
   * Stores the image state and forwards it to the parent component.
   *
   * @param imageState the current state of the image
   */
  const handleState = useCallback((imageState: ImageState) => {
    loadedRef.current = imageState.status === 'loaded' && !!imageState.src
    setState(imageState)
    events.onState?.(imageState)
  }, [])

  /**
   * Toggles the fullscreen modal, once the image has been loaded.
   */
  const handleToggle = useCallback(() => {
    setOpen(prevState => loadedRef.current && !prevState)
  }, [])

  return (
    <>
      <Image
        {...imageProps}
        ref={ref}
        events={{ ...events, onState: handleState, onClick: handleToggle }}
      />
      {loaded && (
        <Dialog
          open={open}
          onClose={handleToggle}
          fullScreen={true}
        >
          <DialogTitle className={classes.header} component={'div'}>
            <Box className={classes.title}>
              <Text
                variant={'14/bold'}
                value={title}
              />
              <Text
                variant={'14/medium'}
                color={'gray.500'}
                value={formatFilesize(props.attachment?.value?.size as number)}
              />
            </Box>
            <IconButton
              onClick={handleToggle}
              sx={{ color: theme.palette.black.main }}
            >
              <Icon name={'close'} />
            </IconButton>
          </DialogTitle>
          <DialogContent className={classes.content}>
            <img
              alt={title}
              src={state?.src as string}
              className={classes.image}
              onClick={handleToggle}
            />
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button
              fullWidth={false}
              variant={'contained'}
              color={'primary'}
              onClick={() => download(state?.src as string, title)}
              value={downloadLabel || 'Download'}
            />
            <Button
              fullWidth={false}
              variant={'text'}
              color={'primary'}
              value={closeLabel || 'Close'}
              onClick={handleToggle}
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default useMemoRef(FullScreen, (props: FullScreenProps) => [
  props.file,
  props.group,
  props.attachment,
  props.value,
])
